import { Injectable } from '@angular/core';

import { IBetslipState } from '../../../../../base/store/state';
import { BetslipType } from '../../../../../core/betslip-type';
import { pickIdFactory } from '../../../../../core/picks/pick-id';
import { ISlip } from '../../../../bet-generation/models';
import { isBetBuilderSlipId, isSingleSlipId, isTeaserSlipId } from '../../../../bet-generation/services/slip.utils';
import { SingleSlipType } from '../../../../bet-generation/slip-type';
import { CriteriaType, RewardTokenContext } from '../../../../reward-tokens/reward-tokens.model';
import { getSelectedBetStationFreeBetToken } from '../../../../reward-tokens/services/betstation-reward-tokens.utils';
import { getSelectedTokenAndEligibilityForContext } from '../../../../reward-tokens/services/linear-reward-tokens.utils';
import { IRewardTokensState } from '../../../../reward-tokens/state';
import { IBetslipTypeState } from '../../../../types/state';
import {
    BetStationRewardTokensPickValidationContext,
    BetStationRewardTokensValidationContext,
    RewardTokensValidationContext,
} from '../../../models/models';

@Injectable()
export class RewardTokensContextGeneratorService {
    generateContext(state: IRewardTokensState, types: IBetslipTypeState, context: RewardTokenContext): RewardTokensValidationContext {
        const [selectedToken, tokenEligibility] = getSelectedTokenAndEligibilityForContext(context, state.eligibilityState, state.tokens, types);
        const hasMaximumStakeError = tokenEligibility?.softCriteriasValidity?.[CriteriaType.MaximumStake] === false;

        return {
            selectedToken,
            selectedTokenEligibilityState: tokenEligibility,
            errors: {
                maximumStake: hasMaximumStakeError,
            },
        };
    }

    generateContextForSlip(state: IRewardTokensState, types: IBetslipTypeState, slip: ISlip): RewardTokensValidationContext {
        const context: RewardTokenContext = this.getSlipContext(slip);

        return this.generateContext(state, types, context);
    }

    generateBetStationContext(state: IBetslipState): BetStationRewardTokensValidationContext {
        const selectedToken = getSelectedBetStationFreeBetToken(state.rewardTokens.betstationTokens, state.rewardTokens.tokens);

        return {
            betslipType: state.types.base.currentSelectedType ?? undefined,
            picks: state.picks.pickList,
            selectedToken,
        };
    }

    generateBetStationPickContext(state: IBetslipState): BetStationRewardTokensPickValidationContext {
        const selectedToken = getSelectedBetStationFreeBetToken(state.rewardTokens.betstationTokens, state.rewardTokens.tokens);

        return {
            selectedToken,
        };
    }

    private getSlipContext(slip: ISlip): RewardTokenContext {
        const slipPickId = Object.keys(slip.slipPicks)[0];

        if (isBetBuilderSlipId(slip.slipId)) {
            return { betslipType: BetslipType.BetBuilder, pickId: pickIdFactory(slipPickId) };
        }

        if (slip.type instanceof SingleSlipType && isSingleSlipId(slip.slipId)) {
            return { betslipType: BetslipType.Single, pickId: pickIdFactory(slipPickId) };
        }

        if (isTeaserSlipId(slip.slipId)) {
            return { betslipType: BetslipType.Teaser };
        }

        return { betslipType: BetslipType.Combo };
    }
}
